import type { FieldValues } from 'react-hook-form';
import { GLYPH } from '../glyphs';
import { Input } from '../Input';
import { Textarea } from '../Textarea';
import { FormField } from './FormField';
import { type BaseFieldProps, describedBy, useBoundField, useFieldIds } from './fieldBase';
import { useRegisterFields } from './fieldRegistry';
import { toLocalizedText } from './localizedText';
import { RetranslateButton } from './retranslate';
import { useLocalizedInput } from './useLocalizedInput';

export interface LocalizedTextFieldProps<TValues extends FieldValues>
  extends BaseFieldProps<TValues> {
  /** Многострочный ввод (textarea), иначе — однострочный. */
  multiline?: boolean;
  rows?: number;
  placeholder?: string;
  /** Максимум символов на язык — счётчик «N / max» для языка ввода. */
  maxLength?: number;
}

/**
 * Поле LocalizedText (COM-09). Языки — из `GET /admin/config` (ContentLanguagesProvider, ARCH-01):
 * RU — один ввод `{ru}`; World — EN и ES с переключателем языка ввода в строке подписи; под полем —
 * исходный язык (`source_lang`) и статус перевода (`translation_status`) — только для показа.
 * Значение поля — объект LocalizedText; ошибки 422 по `поле` и `поле.<язык>` ложатся в это поле.
 */
export function LocalizedTextField<TValues extends FieldValues>({
  name,
  label,
  required,
  hint,
  wide = true,
  disabled,
  multiline = false,
  rows,
  placeholder,
  maxLength,
}: LocalizedTextFieldProps<TValues>) {
  const ids = useFieldIds();
  const { field, fieldState } = useBoundField<TValues>(name);
  const input = useLocalizedInput(toLocalizedText(field.value), field.onChange);
  const { languages } = input;

  // Ошибки сервера по языкам (`title.en`) тоже принадлежат этому полю.
  useRegisterFields(languages.map((code) => `${name}.${code}`));

  const error = input.errorMessage(fieldState.error);
  const hintText =
    hint && input.status ? (
      <>
        {hint} {GLYPH.middot} {input.status}
      </>
    ) : (
      (hint ?? input.status ?? undefined)
    );
  const inputProps = {
    id: ids.id,
    name: `${field.name}.${input.language}`,
    value: input.text,
    lang: input.language,
    placeholder,
    maxLength,
    disabled: disabled || field.disabled,
    onBlur: field.onBlur,
    'aria-required': required || undefined,
    'aria-invalid': error ? true : undefined,
    'aria-describedby': describedBy(Boolean(error), Boolean(hintText), ids),
  } as const;

  return (
    <FormField
      label={label}
      htmlFor={ids.id}
      required={required}
      counter={maxLength ? { length: input.text.length, max: maxLength } : undefined}
      aside={
        <>
          <RetranslateButton fieldName={name} value={toLocalizedText(field.value)} />
          {input.switcher}
        </>
      }
      error={error}
      errorId={ids.errorId}
      hint={hintText}
      hintId={ids.hintId}
      wide={wide}
    >
      {multiline ? (
        <Textarea
          {...inputProps}
          ref={field.ref}
          rows={rows}
          onChange={(event) => input.change(event.target.value)}
        />
      ) : (
        <Input
          {...inputProps}
          ref={field.ref}
          onChange={(event) => input.change(event.target.value)}
        />
      )}
    </FormField>
  );
}
